import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "./api";

const HEADLESS = "/_allauth/browser/v1";

// allauth.headless replies 401 to a *successful* password reset / email
// verification when the operation doesn't log the user in — the 401 is the
// "you may now authenticate" flow state, not a failure.
async function allauthOk(call: Promise<unknown>): Promise<void> {
  try {
    await call;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return;
    throw err;
  }
}

// Login/signup reply 409 when the session is *already* authenticated —
// e.g. a stale login page after a password reset elsewhere in the browser.
// The user is signed in, so treat it as success; the session refetch in
// onSuccess drives the usual redirect.
async function allauthOkIfSignedIn(call: Promise<unknown>): Promise<void> {
  try {
    await call;
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) return;
    throw err;
  }
}

// A 401 whose flow state includes a pending "verify_email" means the
// credentials were fine but the address still awaits verification.
export function isVerificationPending(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 401) return false;
  const body = err.body as {
    data?: { flows?: { id?: string; is_pending?: boolean }[] };
  } | null;
  return Boolean(
    body?.data?.flows?.some((f) => f.id === "verify_email" && f.is_pending)
  );
}

// allauth.headless validation failures look like {errors: [{message, ...}]}.
export function allauthErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { errors?: { message?: string }[] } | null;
    const message = body?.errors?.[0]?.message;
    if (message) return message;
  }
  return fallback;
}

export type SessionUser = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: "platform_admin" | "org_admin" | "team_lead" | "member" | null;
  org_role: "org_admin" | "team_lead" | "member" | null;
  actual_role: "platform_admin" | "org_admin" | "team_lead" | "member" | null;
  is_platform_admin: boolean;
  organization_id: number | null;
  organization_slug: string | null;
  features: string[];
  capabilities: string[];
  managed_departments: { id: number; name: string }[];
  department: string;
  job_title: string;
  phone: string;
  timezone: string | null;
  linkedin_url: string;
  avatar_url: string | null;
  blob_uploads_enabled: boolean;
};

export function hasCapability(
  user: SessionUser | null | undefined,
  capability: string,
): boolean {
  return !!user?.capabilities?.includes(capability);
}

// Feature-flag check against the session's active flag keys. Flags are
// managed in Django admin (Scorecards → Feature flags); platform admins get
// every flag server-side.
export function hasFeature(
  user: SessionUser | null | undefined,
  key: string,
): boolean {
  return !!user?.features?.includes(key);
}

export type Session = {
  user: SessionUser | null;
};

export type ProfileUpdateInput = {
  first_name?: string;
  last_name?: string;
  department?: string;
  job_title?: string;
  phone?: string;
  timezone?: string;
  linkedin_url?: string;
};

export const sessionQueryKey = ["session"] as const;

export function useSession() {
  return useQuery<Session>({
    queryKey: sessionQueryKey,
    queryFn: () => apiFetch<Session>("/api/accounts/session/"),
    staleTime: 60_000,
  });
}

type LoginInput = { email: string; password: string };

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) =>
      allauthOkIfSignedIn(
        apiFetch(`${HEADLESS}/auth/login`, {
          method: "POST",
          json: input,
        })
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionQueryKey }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch("/api/accounts/logout/", {
        method: "POST",
      }),
    onSuccess: () => qc.setQueryData<Session>(sessionQueryKey, { user: null }),
  });
}

type ForgotPasswordInput = { email: string };

export function useForgotPassword() {
  return useMutation({
    mutationFn: (input: ForgotPasswordInput) =>
      apiFetch(`${HEADLESS}/auth/password/request`, {
        method: "POST",
        json: input,
      }),
  });
}

export function useResetPasswordKeyCheck(key: string) {
  return useQuery({
    queryKey: ["password-reset-key", key],
    queryFn: () =>
      apiFetch(`${HEADLESS}/auth/password/reset`, {
        headers: { "X-Password-Reset-Key": key },
      }),
    retry: false,
  });
}

type ResetPasswordInput = { key: string; password: string };

export function useResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ResetPasswordInput) =>
      allauthOk(
        apiFetch(`${HEADLESS}/auth/password/reset`, {
          method: "POST",
          json: input,
        })
      ),
    // Resetting while a session is live keeps the user signed in — refetch
    // so pages reflect the real authentication state.
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionQueryKey }),
  });
}

export function useVerifyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      allauthOk(
        apiFetch(`${HEADLESS}/auth/email/verify`, {
          method: "POST",
          json: { key },
        })
      ),
    // Verification may log the user in (e.g. fresh signup in same browser).
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionQueryKey }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfileUpdateInput) =>
      apiFetch<Session>("/api/accounts/profile/", {
        method: "PATCH",
        json: input,
      }),
    onSuccess: (session) => qc.setQueryData<Session>(sessionQueryKey, session),
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetch<Session>("/api/accounts/profile/avatar/", {
        method: "POST",
        body: form,
      });
    },
    onSuccess: (session) => qc.setQueryData<Session>(sessionQueryKey, session),
  });
}

export function useRemoveAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<Session>("/api/accounts/profile/avatar/", { method: "DELETE" }),
    onSuccess: (session) => qc.setQueryData<Session>(sessionQueryKey, session),
  });
}
